'use client'

import React from 'react'
import { setCookie } from 'cookies-next'
import { useRegionStore } from '../../../store/regionStore'
import { useShippingCountry } from './useShippingCountry'
import MyAutocomplete from '../index'
import { COUNTRY_COOKIE, REGION_COOKIE, countryToRegion, isRegion, pathWithRegion } from '../../../utils/regions'
import usePathnameCompat from '../../../utils/usePathnameCompat'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

// #118 (server-chrome, Step 1): explicit client island. `countries` and
// `selectedCountryCode` are now OPTIONAL — when omitted (the footer picker) the
// narrow useShippingCountry() selector supplies state.countries /
// state.shippingCountry, so the footer no longer prop-drills them. Call sites
// that need a DIFFERENT list or a controlled value (AddressInput's billing
// country, ShippingInfoSection's filtered list) keep passing the props and those
// win — the hook fallback only kicks in when a prop is undefined.
function CountriesAutocomplete({ countries: countriesProp, onChangeOverride, selectedCountryCode: selectedCountryCodeProp,
                                 inputTextColor, popupIndicatorColor,
                                 fontSizeAdjust, maxWidth, transparentBorder, isUpperCase, dark, noPadding, staticWidth, fullWidth, staticWidthLg }) {
  const seam = useShippingCountry()
  const setShippingCountry = useRegionStore((s) => s.setShippingCountry)
  const currentPath = usePathnameCompat()

  const countries = countriesProp !== undefined ? countriesProp : seam.countries
  const selectedCountryCode = selectedCountryCodeProp !== undefined ? selectedCountryCodeProp : seam.shippingCountry

  const selectedCountry = countries.find(c => c.code === selectedCountryCode)

  // Selecting a country:
  //  1. persists the precise shipping country (transit/ships-from delta cookie),
  //  2. HARD-navigates to the country's BACKEND STORE (us|eu|au|world) so the
  //     server SSRs that store's prices and the fresh document hydrates from them.
  // The backend `store` field on the country option is the single source of
  // truth (incl. /world); we only fall back to the legacy countryToRegion when a
  // store is somehow missing from the option. A served=false country still has a
  // store (always "world") and still lands on /world — the can't-ship notice is
  // applied client-side from the `served` flag, not here.
  //
  // #188 — HARD navigation (window.location.assign), NOT a client soft nav. A
  // region change means loading the target region's SSR page, which already bakes
  // in that region's catalog/pricing — so there is no client /api refetch and no
  // client-side store mutation to re-price. This mirrors SmartLink's default (a
  // real <a href>): the target SSR is served from the parent's page cache with
  // ZERO render-worker load, whereas an App-Router soft nav (router.push) would
  // fetch an uncacheable RSC flight that always hits the single render worker (the
  // saturation this task exists to remove). Cookies are set BEFORE navigating so
  // the new document's SSR + checkout see the chosen store (region cookie) and the
  // precise shipping country (country cookie); the cart survives in localStorage.
  const handleCountryChange = (newValue) => {
    if (!newValue || !newValue.code) {
      return
    }

    const country = newValue.code
    const region = isRegion(newValue.store) ? String(newValue.store).toLowerCase() : countryToRegion(country)

    // Optimistically reflect the pick in the picker immediately, so it never
    // appears to "revert" in the brief window before the new document loads.
    setShippingCountry(country)

    setCookie(COUNTRY_COOKIE, country, { maxAge: COOKIE_MAX_AGE, path: '/' })
    setCookie(REGION_COOKIE, region, { maxAge: COOKIE_MAX_AGE, path: '/' })

    const target = pathWithRegion(currentPath, region)
    if (typeof window !== 'undefined') {
      window.location.assign(target)
    }
  }

  return (
    <MyAutocomplete
      isLoading={seam.isLoading}
      options={countries}
      getOptionLabel={(option) => option && option.name}
      value={selectedCountry || null}
      onChange={(newValue) => onChangeOverride ? onChangeOverride(newValue) : handleCountryChange(newValue)}
      inputTextColor={inputTextColor}
      popupIndicatorColor={popupIndicatorColor}
      fontSizeAdjust={fontSizeAdjust}
      maxWidth={maxWidth}
      transparentBorder={transparentBorder}
      isUpperCase={isUpperCase}
      dark={dark}
      noPadding={noPadding}
      staticWidth={staticWidth}
      fullWidth={fullWidth}
      staticWidthLg={staticWidthLg}
      ariaLabel="Select shipping country"
      autoComplete="country-name"
      name="country"
      placeholder="SELECT COUNTRY"
    />
  )
}

export default CountriesAutocomplete
